/**
 * Query Service
 * Handles Query Module API calls (CRUD, schema, execution, CSV export).
 */
import BaseApiService from '../base/BaseApiService';
import type {
  QuerySchemaResponse,
  QueryListResponse,
  ListQueriesParams,
  Query,
  CreateQueryRequest,
  UpdateQueryRequest,
  MutateQueryResponse,
  DeleteQueryResponse,
  ExecuteQueryRequest,
  QueryResultsResponse,
  PreviewQueryRequest,
  QueryPreviewResponse,
} from '@app-types';

class QueryService extends BaseApiService {
  /** Dynamic schema (entities, fields, relationships) for the builder. */
  async getSchema(): Promise<QuerySchemaResponse> {
    return this.get<QuerySchemaResponse>('/queries/schema');
  }

  /**
   * List queries available to the user (own Personal + all Global) with
   * server-side type-filter, search, sort and pagination (US-02).
   */
  async list(params: ListQueriesParams = {}): Promise<QueryListResponse> {
    return this.get<QueryListResponse>('/queries', params);
  }

  async getById(id: number): Promise<Query> {
    return this.get<Query>(`/queries/${id}`);
  }

  async create(data: CreateQueryRequest): Promise<MutateQueryResponse> {
    return this.post<MutateQueryResponse>('/queries', data);
  }

  async update(id: number, data: UpdateQueryRequest): Promise<MutateQueryResponse> {
    return this.put<MutateQueryResponse>(`/queries/${id}`, data);
  }

  async remove(id: number): Promise<DeleteQueryResponse> {
    return this.delete<DeleteQueryResponse>(`/queries/${id}`);
  }

  /** Execute a query against live data (paginated). */
  async execute(id: number, body: ExecuteQueryRequest = {}): Promise<QueryResultsResponse> {
    return this.post<QueryResultsResponse>(`/queries/${id}/execute`, body);
  }

  /**
   * Preview an unsaved query definition against live data without persisting it
   * (US-08 "Run" in the builder, #863).
   */
  async preview(body: PreviewQueryRequest): Promise<QueryPreviewResponse> {
    return this.post<QueryPreviewResponse>('/queries/preview', body);
  }

  /**
   * Fetch the full result set as a CSV Blob (US-09). The backend streams the
   * UTF-8 (with BOM) text/csv response; requesting a Blob avoids buffering the
   * whole export as a JS string for large result sets.
   */
  async exportCsv(id: number): Promise<Blob> {
    return this.getBlob(`/queries/${id}/execute/csv`);
  }
}

export const queryService = new QueryService();
export default queryService;
